import {
  Stack,
  Tabs,
  Title,
  Text,
  Container,
  Center,
  Loader,
  Alert,
} from '@mantine/core';
import { notify } from '@/lib/notify';
import { IconBuilding, IconStethoscope, IconUsers } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useMedplumDirectoryUsers } from '../../hooks/useMedplumDirectoryUsers';
import { canNavigateToUserProfilePage, providerUserProfilePath } from '../../lib/userProfileRouteAccess';
import { updatePracticeSettings } from '../../lib/practiceSettingsService';
import { usePermissions } from '../../contexts/PermissionContext';
import { filterUsersForPractice } from '../../lib/adminDirectoryService';
import { isUnifyPlatformStaffRole } from '@unify/fhir-model/staff-role-org';
import { type Practice, type User, userIsPractitionerEntry } from './types/rbac';
import { CreatePracticeModal } from './components/CreatePracticeModal';
import { CreateUserModal } from './components/CreateUserModal';
import { StaffAddClientModal } from './components/StaffAddClientModal';
import { PracticeSettings } from '../settings/components/PracticeSettings';
import { PracticesPage } from './PracticesPage';
import { UsersPage } from './UsersPage';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

const USER_TABS = ['practices', 'practitioners', 'clients'] as const;
const USER_ONLY_TABS = ['practitioners', 'clients'] as const;

/** Map legacy tab slugs from bookmarks / back navigation to current tabs. */
function normalizeUserTab(tab: string | undefined): string | undefined {
  if (tab == null) return undefined;
  if (tab === 'providers' || tab === 'staff') return 'practitioners';
  if (tab === 'permissions') return undefined;
  return tab;
}

function allowedUserTabs(hidePracticesTab: boolean): readonly string[] {
  return hidePracticesTab ? USER_ONLY_TABS : USER_TABS;
}

export interface UsersSectionPageProps {
  /** When set, page is scoped to this practice: only users from this practice, no practice list. */
  scopePractice?: Practice;
  /** When true, hide the Practices tab and show only Practitioners and Clients. */
  hidePracticesTab?: boolean;
}

export function UsersSectionPage({
  scopePractice,
  hidePracticesTab = false,
}: UsersSectionPageProps = {}): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const medplum = useMedplum();
  const sessionProfile = useMedplumProfile();
  const { role: sessionRole, permissions } = usePermissions();
  const canResendInvite = permissions.edit_users === true;
  const canInvitePractitioners =
    isUnifyPlatformStaffRole(sessionRole) && permissions.edit_users === true;
  const initialTab = hidePracticesTab ? 'practitioners' : 'practices';
  const [activeTab, setActiveTab] = useState<string | null>(initialTab);

  // Restore tab when returning from user settings (back button passes state.tab)
  useEffect(() => {
    const tab = normalizeUserTab(location.state?.tab);
    const allowedTabs = allowedUserTabs(hidePracticesTab);
    if (tab != null && allowedTabs.includes(tab)) setActiveTab(tab);
  }, [location.state?.tab, hidePracticesTab]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreatePractice, setShowCreatePractice] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [selectedPracticeStatusFilter, setSelectedPracticeStatusFilter] = useState<string | null>('');
  const [selectedPractice, setSelectedPractice] = useState<Practice | null>(null);
  const {
    users,
    setUsers,
    practices,
    setPractices,
    clientIdsByOrganizationId,
    loading: directoryLoading,
    error: directoryError,
    createPractice,
    createUser,
    reload,
    registerClientForOrganization,
    platformOrganization,
  } = useMedplumDirectoryUsers();

  // The directory provider spans every Settings route, so its first fetch can predate a
  // profile edit made in another section. Refetch each time this page mounts.
  useEffect(() => {
    reload();
  }, [reload]);

  const [practiceSortColumn, setPracticeSortColumn] = useState<'name' | 'ownerName' | 'userCount' | 'createdAt'>('name');
  const [practiceSortDirection, setPracticeSortDirection] = useState<'asc' | 'desc'>('asc');

  const filteredPractices = useMemo(() => {
    return practices.filter((practice) => {
      const matchesSearch =
        practice.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        practice.ownerName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = selectedPracticeStatusFilter
        ? practice.status === selectedPracticeStatusFilter
        : true;

      return matchesSearch && matchesStatus;
    });
  }, [practices, searchQuery, selectedPracticeStatusFilter]);

  const sortedPractices = useMemo(() => {
    const sorted = [...filteredPractices].sort((a, b) => {
      let comparison = 0;
      if (practiceSortColumn === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (practiceSortColumn === 'ownerName') {
        comparison = a.ownerName.localeCompare(b.ownerName);
      } else if (practiceSortColumn === 'userCount') {
        comparison = a.userCount - b.userCount;
      } else if (practiceSortColumn === 'createdAt') {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        comparison = Number.isNaN(dateA) || Number.isNaN(dateB) ? String(a.createdAt).localeCompare(String(b.createdAt)) : dateA - dateB;
      }
      return practiceSortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredPractices, practiceSortColumn, practiceSortDirection]);

  const scopedUsers = useMemo(() => {
    if (scopePractice == null) return users;
    return filterUsersForPractice(users, scopePractice.id, clientIdsByOrganizationId);
  }, [users, scopePractice, clientIdsByOrganizationId]);

  const practitionersUsers = useMemo(
    () => scopedUsers.filter((u) => userIsPractitionerEntry(u)),
    [scopedUsers],
  );
  const clientsUsers = useMemo(
    () => scopedUsers.filter((u) => !userIsPractitionerEntry(u)),
    [scopedUsers],
  );

  const practiceUsersForDetail = useMemo(() => {
    if (!selectedPractice) return [];
    return filterUsersForPractice(users, selectedPractice.id, clientIdsByOrganizationId);
  }, [users, selectedPractice, clientIdsByOrganizationId]);

  const handleViewUser = (user: User) => {
    if (user.role !== 'client' && !canNavigateToUserProfilePage(user.id, sessionProfile?.id, sessionRole)) {
      notify({
        title: 'Access denied',
        message: 'You can only open your own profile. Ask a practice or platform admin to manage other users.',
        type: 'error',
      });
      return;
    }
    navigate(providerUserProfilePath(user.id, user.role, sessionProfile?.id), { state: { fromTab: activeTab } });
  };
  const handleResendInvite = async (email: string) => {
    const { resendInvite } = await import('../../lib/adminDirectoryService');
    await resendInvite(medplum, email);
  };
  const handleViewPracticeSettings = (practice: Practice) => setSelectedPractice(practice);
  const handleBackFromPracticeSettings = () => setSelectedPractice(null);

  const practicesForModal = scopePractice != null ? [scopePractice] : practices;

  const resolvedActiveTab =
    hidePracticesTab && activeTab === 'practices' ? 'practitioners' : activeTab;

  if (selectedPractice && scopePractice == null) {
    return (
      <PracticeSettings
        practice={selectedPractice}
        directoryUsers={users}
        onBack={handleBackFromPracticeSettings}
        usePracticeNameAsTitle
        practiceUsers={practiceUsersForDetail}
        onUpdatePractice={async (id, updates): Promise<boolean> => {
          const updatedPractices = practices.map((p) => (p.id === id ? { ...p, ...updates } : p));
          setPractices(updatedPractices);
          setSelectedPractice(updatedPractices.find((p) => p.id === id) || null);
          try {
            await updatePracticeSettings(medplum, id, updates);
            reload();
            return true;
          } catch (err) {
            const msg = getUserFacingErrorMessage(err);
            notify({ title: 'Could not save settings', message: msg, type: 'error' });
            throw err;
          }
        }}
      />
    );
  }

  const isScopedUsersOnly = scopePractice != null && hidePracticesTab;

  if (directoryLoading && users.length === 0) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (directoryError) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Could not load directory">
          {getUserFacingErrorMessage(directoryError)}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>{isScopedUsersOnly ? 'Users' : 'Practices & Users'}</Title>
          <Text c="gray.6" size="sm" mt="xs">
            {isScopedUsersOnly
              ? 'Manage users in your practice'
              : 'Manage practices and users across the platform'}
          </Text>
        </div>

        <Tabs value={resolvedActiveTab} onChange={setActiveTab}>
          <Tabs.List styles={{ list: { borderBottom: 'none' } }}>
            {!hidePracticesTab && (
              <Tabs.Tab
                value="practices"
                leftSection={<IconBuilding size={16} />}
                style={{ fontWeight: 400 }}
              >
                Practices
              </Tabs.Tab>
            )}
            <Tabs.Tab
              value="practitioners"
              leftSection={<IconStethoscope size={16} />}
              style={{ fontWeight: 400 }}
            >
              Practitioners
            </Tabs.Tab>
            <Tabs.Tab
              value="clients"
              leftSection={<IconUsers size={16} />}
              style={{ fontWeight: 400 }}
            >
              Clients
            </Tabs.Tab>
          </Tabs.List>

          {!hidePracticesTab && (
            <Tabs.Panel value="practices" mt="md">
              <PracticesPage
                practices={practices}
                filteredPractices={sortedPractices}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                statusFilter={selectedPracticeStatusFilter}
                onStatusChange={setSelectedPracticeStatusFilter}
                onViewSettings={handleViewPracticeSettings}
                onCreatePractice={() => setShowCreatePractice(true)}
                canCreatePractice={sessionRole === 'unify-admin'}
                sortColumn={practiceSortColumn}
                sortDirection={practiceSortDirection}
                onSort={(column) => {
                  setPracticeSortColumn((prevCol) => {
                    if (prevCol === column) {
                      setPracticeSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
                      return prevCol;
                    }
                    setPracticeSortDirection('asc');
                    return column;
                  });
                }}
              />
            </Tabs.Panel>
          )}

          <Tabs.Panel value="practitioners" mt="md">
            <UsersPage
              users={practitionersUsers}
              practices={practicesForModal}
              scopePracticeId={scopePractice?.id}
              scopePractice={scopePractice}
              onViewUser={handleViewUser}
              onUpdateUser={(id, updates) =>
                setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...updates } : u)))
              }
              onCreateUser={() => setShowCreateUser(true)}
              hideCreateUserButton={!canInvitePractitioners}
              onResendInvite={canResendInvite ? handleResendInvite : undefined}
            />
          </Tabs.Panel>
          <Tabs.Panel value="clients" mt="md">
            <UsersPage
              users={clientsUsers}
              practices={practicesForModal}
              clientIdsByOrganizationId={clientIdsByOrganizationId}
              scopePracticeId={scopePractice?.id}
              scopePractice={scopePractice}
              onViewUser={handleViewUser}
              onUpdateUser={(id, updates) =>
                setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...updates } : u)))
              }
              onCreateUser={() => setShowAddClientModal(true)}
              onCreateClient={() => setShowAddClientModal(true)}
              hideCreateUserButton
              hideRoles
              onResendInvite={canResendInvite ? handleResendInvite : undefined}
            />
          </Tabs.Panel>
        </Tabs>

        {showAddClientModal && (
          <StaffAddClientModal
            opened
            onClose={() => {
              setShowAddClientModal(false);
              reload();
            }}
            practice={scopePractice}
            practices={practicesForModal}
            onCreated={(user, context) => {
              if (context?.organizationId) {
                registerClientForOrganization(user.id, context.organizationId);
              }
              setUsers((prev) => (prev.some((u) => u.id === user.id) ? prev : [...prev, user]));
            }}
          />
        )}

        {showCreatePractice && sessionRole === 'unify-admin' && (
          <CreatePracticeModal
            onClose={() => setShowCreatePractice(false)}
            onCreatePractice={async (payload) => {
              await createPractice(payload);
              setShowCreatePractice(false);
            }}
          />
        )}

        {showCreateUser && canInvitePractitioners && (
          <CreateUserModal
            onClose={() => setShowCreateUser(false)}
            onCreateUser={async (payload) => {
              const user = await createUser(payload);
              setShowCreateUser(false);
              return user;
            }}
            practices={practicesForModal}
            isPlatformView={scopePractice == null}
            currentPracticeId={scopePractice?.id}
            platformOrganization={scopePractice == null ? platformOrganization : null}
            defaultRole="provider"
          />
        )}
      </Stack>
    </Container>
  );
}
